import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, TextInput, Spinner, StaticSelect, showToast } from "../../../components/shared";
import { EmptyState } from "../../../components/shared";
import { HiOutlineClipboardDocumentList } from "react-icons/hi2";
import { useCrossAccountTasks, useCrossAccountProjects } from "../../../lib/react-query/crossAccount";
import { useUpdateTaskStatus } from "../../../lib/react-query/tasks";
import TaskCard from "../../../components/Task/TaskCard";
import { DndContext, DragOverlay, useDroppable, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const TASK_PRIORITY_OPTIONS = [
  { id: "LOW", name: "Low" },
  { id: "MEDIUM", name: "Medium" },
  { id: "HIGH", name: "High" },
];

const ORDERING_OPTIONS = [
  { id: "-updated_at", name: "Recently Updated" },
  { id: "updated_at", name: "Least Recently Updated" },
  { id: "-created_at", name: "Newest First" },
  { id: "created_at", name: "Oldest First" },
  { id: "-priority", name: "Priority (High to Low)" },
  { id: "priority", name: "Priority (Low to High)" },
  { id: "-due_date", name: "Due Date (Soonest)" },
  { id: "due_date", name: "Due Date (Latest)" },
  { id: "-title", name: "Title (A-Z)" },
  { id: "title", name: "Title (Z-A)" },
];

const Column = ({ id, title, items, children }) => {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={`border rounded-xl bg-white p-3 border-t-2 ${isOver ? "ring-2 ring-secondary/30" : ""} ${
        id === "pending"
          ? "border-t-warning/60"
          : id === "active"
          ? "border-t-secondary/60"
          : "border-t-success/60"
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <p className="font-sans text-sm font-semibold">{title}</p>
        <span className="inline-flex items-center gap-1.5 text-[11px] text-darkgrey">
          <span
            className={`w-1.5 h-1.5 rounded-full ${
              id === "pending" ? "bg-warning" : id === "active" ? "bg-secondary" : "bg-success/100"
            }`}
          />
          {items?.length || 0}
        </span>
      </div>
      <div className="flex flex-col gap-3 min-h-[200px]">{children}</div>
    </div>
  );
};

const DraggableTask = ({ task, container, onOpen }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `task-${task?.id}`,
    data: { container, taskId: task?.id },
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 999 : "auto",
    opacity: isDragging ? 0.4 : 1,
    cursor: "grab",
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      role="button"
      tabIndex={0}
      aria-label={`Open task ${task?.title || task?.id}`}
      onClick={() => onOpen?.(task)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen?.(task);
        }
      }}
    >
      <TaskCard task={task} draggable />
    </div>
  );
};

const MyTasks = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get("search") || "");
  const [priority, setPriority] = useState(searchParams.get("priority") || "");
  const [ordering, setOrdering] = useState(searchParams.get("ordering") || "-updated_at");
  const [projectFilter, setProjectFilter] = useState(searchParams.get("project") || "");
  const [activeId, setActiveId] = useState(null);
  const [page, setPage] = useState(1);
  const [allTasks, setAllTasks] = useState([]);
  const filtersRef = useRef({ debouncedSearch, priority, ordering, projectFilter });

  // Sync filter state to URL params
  useEffect(() => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set("search", debouncedSearch);
    if (priority) params.set("priority", priority);
    if (ordering && ordering !== "-updated_at") params.set("ordering", ordering);
    if (projectFilter) params.set("project", projectFilter);
    setSearchParams(params, { replace: true });
  }, [debouncedSearch, priority, ordering, projectFilter, setSearchParams]);

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 8 },
  });
  const sensors = useSensors(pointerSensor);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset to page 1 when filters change
  useEffect(() => {
    const prev = filtersRef.current;
    if (
      prev.debouncedSearch !== debouncedSearch ||
      prev.priority !== priority ||
      prev.ordering !== ordering ||
      prev.projectFilter !== projectFilter
    ) {
      setPage(1);
      setAllTasks([]);
      filtersRef.current = { debouncedSearch, priority, ordering, projectFilter };
    }
  }, [debouncedSearch, priority, ordering, projectFilter]);

  const { data: tasksData, isLoading, isFetching } = useCrossAccountTasks(
    {
      search: debouncedSearch,
      priority,
      ordering,
      project_id: projectFilter ? Number(projectFilter) : undefined,
      page,
      page_size: 50,
    },
    true
  );

  // Accumulate tasks across pages
  useEffect(() => {
    if (!tasksData?.items) return;
    if (page === 1) {
      setAllTasks(tasksData.items);
    } else {
      setAllTasks((prev) => {
        const existingIds = new Set(prev.map((t) => t.id));
        const newItems = tasksData.items.filter((t) => !existingIds.has(t.id));
        return [...prev, ...newItems];
      });
    }
  }, [tasksData, page]);

  const { data: projectsData } = useCrossAccountProjects({ page_size: 100 });
  const projectOptions = useMemo(() => {
    return (projectsData?.items || []).map((p) => ({ id: String(p.id), name: p.title }));
  }, [projectsData]);

  const { mutateAsync: updateTaskStatus } = useUpdateTaskStatus();

  const count = tasksData?.count || 0;
  const totalPages = tasksData?.total_pages || 1;
  const hasMore = page < totalPages;
  const handleLoadMore = useCallback(() => setPage((p) => p + 1), []);

  // Group flat task list into 3 status buckets
  const grouped = useMemo(() => {
    const result = { pending: [], active: [], done: [] };
    for (const task of allTasks) {
      if (task.status === "PENDING") result.pending.push(task);
      else if (task.status === "ACTIVE") result.active.push(task);
      else if (task.status === "DONE") result.done.push(task);
    }
    return result;
  }, [allTasks]);

  const [board, setBoard] = useState({ pending: [], active: [], done: [] });

  useEffect(() => {
    setBoard(grouped);
  }, [grouped]);

  const handleTaskClick = (task) => {
    navigate(`/projects/${task.project}/tasks/${task.id}`);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over) return;

    const taskId = String(active?.id || "").replace("task-", "");
    const from = active?.data?.current?.container;
    let to = over?.id;
    if (!taskId || !from || !to) return;

    // If dropped on a task, resolve to its column
    if (String(to).startsWith("task-")) {
      const targetTaskId = String(to).replace("task-", "");
      const findContainer = (tid) => {
        const sid = String(tid);
        if (board.pending.some((t) => String(t.id) === sid)) return "pending";
        if (board.active.some((t) => String(t.id) === sid)) return "active";
        if (board.done.some((t) => String(t.id) === sid)) return "done";
        return null;
      };
      to = findContainer(targetTaskId) || to;
    }

    // Only handle cross-column moves (status changes), no within-column reorder
    if (from === to) return;
    if (to !== "pending" && to !== "active" && to !== "done") return;

    const next = { ...board };
    const moved = board[from].find((t) => String(t.id) === String(taskId));
    if (!moved) return;

    next[from] = board[from].filter((t) => String(t.id) !== String(taskId));
    next[to] = [...board[to], moved];
    setBoard(next);

    const status = to === "pending" ? "PENDING" : to === "active" ? "ACTIVE" : "DONE";
    try {
      await updateTaskStatus({ taskId, payload: { status }, projectId: moved.project });
    } catch (e) {
      // Revert on failure
      setBoard(grouped);
      const msg = e?.response?.data?.message || "Failed to update task status";
      showToast(msg, "error", { position: "top-center" });
    }
  };

  return (
    <div className="w-full max-w-7xl mx-auto py-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm mb-6">
        <span className="text-grey">Dashboard</span>
        <span className="text-darkgrey">/</span>
        <span className="text-darkgrey font-medium">My Tasks</span>
      </nav>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-darkgrey">My Tasks</h1>
          <p className="text-sm text-grey mt-1">
            {count > 0 ? `${count} tasks across your projects` : "No tasks found"}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-4 mb-6">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <TextInput placeholder="Search tasks..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="lg:w-48">
            <StaticSelect
              label="Priority"
              placeholder="All Priorities"
              options={TASK_PRIORITY_OPTIONS}
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
            />
          </div>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="sm:w-48">
            <StaticSelect
              label="Project"
              placeholder="All Projects"
              options={projectOptions}
              value={projectFilter}
              onChange={(e) => setProjectFilter(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2 ml-auto sm:w-56">
            <StaticSelect
              label="Sort by"
              placeholder="Sort by"
              options={ORDERING_OPTIONS}
              value={ordering}
              onChange={(e) => setOrdering(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Kanban Board */}
      {isLoading && board.pending.length === 0 && board.active.length === 0 && board.done.length === 0 ? (
        <div className="flex items-center justify-center py-16">
          <Spinner size="lg" />
        </div>
      ) : !isLoading && board.pending.length === 0 && board.active.length === 0 && board.done.length === 0 ? (
        <EmptyState
          icon={HiOutlineClipboardDocumentList}
          title="No tasks yet"
          description="Tasks assigned to you across all projects will appear here. Create a task in any project to get started."
        />
      ) : (
        <DndContext
          sensors={sensors}
          onDragStart={(e) => setActiveId(e.active.id)}
          onDragEnd={handleDragEnd}
          onDragCancel={() => setActiveId(null)}
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 items-stretch gap-4 pb-6">
            <Column id="pending" title="Pending" items={board.pending}>
              <SortableContext items={board.pending.map((t) => `task-${t.id}`)} strategy={verticalListSortingStrategy}>
                {board.pending.length > 0 ? (
                  board.pending.map((task) => (
                    <DraggableTask
                      key={task.id}
                      task={task}
                      container="pending"
                      onOpen={handleTaskClick}
                    />
                  ))
                ) : (
                  <p className="text-sm text-grey text-center py-8">No pending tasks</p>
                )}
              </SortableContext>
            </Column>

            <Column id="active" title="Active" items={board.active}>
              <SortableContext items={board.active.map((t) => `task-${t.id}`)} strategy={verticalListSortingStrategy}>
                {board.active.length > 0 ? (
                  board.active.map((task) => (
                    <DraggableTask
                      key={task.id}
                      task={task}
                      container="active"
                      onOpen={handleTaskClick}
                    />
                  ))
                ) : (
                  <p className="text-sm text-grey text-center py-8">No active tasks</p>
                )}
              </SortableContext>
            </Column>

            <Column id="done" title="Done" items={board.done}>
              <SortableContext items={board.done.map((t) => `task-${t.id}`)} strategy={verticalListSortingStrategy}>
                {board.done.length > 0 ? (
                  board.done.map((task) => (
                    <DraggableTask
                      key={task.id}
                      task={task}
                      container="done"
                      onOpen={handleTaskClick}
                    />
                  ))
                ) : (
                  <p className="text-sm text-grey text-center py-8">No completed tasks</p>
                )}
              </SortableContext>
            </Column>
          </div>

          {hasMore && (
            <div className="flex justify-center pb-6">
              <Button
                variant="outlined"
                color="secondary"
                size="md"
                loading={isFetching}
                onClick={handleLoadMore}
                className="px-6 font-sans"
              >
                {`Load more tasks (${allTasks.length} of ${count})`}
              </Button>
            </div>
          )}

          <DragOverlay>
            {activeId
              ? (() => {
                  const sid = String(activeId).replace("task-", "");
                  const t =
                    board.pending.find((x) => String(x.id) === sid) ||
                    board.active.find((x) => String(x.id) === sid) ||
                    board.done.find((x) => String(x.id) === sid);
                  return t ? <TaskCard task={t} /> : null;
                })()
              : null}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  );
};

export default MyTasks;
